import * as React from "react"
import {
  type Party,
  PartyRole,
  useListActivePartyRolesQuery,
  useReadPartyQuery,
} from "@digits-graphql/frontend/graphql-bearer"
import useRouter from "@digits-shared/hooks/useRouter"
import { useViewVersion } from "src/frontend/components/Shared/Contexts/useViewVersion"
import { FrontendPartyRole, SUPPORTED_PARTY_ROLES } from "src/frontend/types/FrontendPartyRole"

export interface PartyDetailsContextProps {
  partyId: string
  partyRole: PartyRole
  party?: Party
  loading: boolean
  activePartyRoles?: PartyRole[]
}

export const DEFAULT_PARTY_DETAILS_CONTEXT: PartyDetailsContextProps = {
  partyId: "",
  partyRole: PartyRole.UnknownRole,
  loading: false,
}

export const PartyDetailsContext = React.createContext<PartyDetailsContextProps>(
  DEFAULT_PARTY_DETAILS_CONTEXT
)

export function usePartyDetails() {
  return React.useContext(PartyDetailsContext)
}

export function useBuildPartyDetailsContext() {
  const { params } = useRouter()
  const { partyId = "", partyRole } = params
  const viewKey = useViewVersion()

  const { data: partyQuery, loading: loadingParty } = useReadPartyQuery({
    variables: {
      partyId,
      viewKey,
    },
  })
  const party = partyQuery?.readParty

  const { data, loading: loadingRoles } = useListActivePartyRolesQuery({
    variables: {
      filter: {
        viewKey,
        partyId,
      },
    },
    context: { avsBatch: true },
    skip: !partyId,
  })

  const activePartyRoles = React.useMemo(
    () =>
      data?.listActivePartyRoles
        .filter((r) => SUPPORTED_PARTY_ROLES.includes(r))
        .sort((a, b) => SUPPORTED_PARTY_ROLES.indexOf(a) - SUPPORTED_PARTY_ROLES.indexOf(b)),
    [data?.listActivePartyRoles]
  )

  return React.useMemo((): {
    partyDetailsContext: PartyDetailsContextProps
  } => {
    const frontendPartyRole = FrontendPartyRole.findByURLKey(partyRole)

    return {
      partyDetailsContext: {
        party,
        loading: loadingParty || loadingRoles,
        partyRole: frontendPartyRole.partyRole,
        partyId,
        activePartyRoles,
      },
    }
  }, [partyRole, party, loadingParty, loadingRoles, partyId, activePartyRoles])
}
